import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { SkeletonPage } from '@/lib/design'
import RouteErrorPage from '@/components/errors/RouteErrorPage'

import Landing  from '@/pages/landing/Landing'
import Login    from '@/pages/auth/Login'
import Register from '@/pages/auth/Register'

const AppShell        = lazy(() => import('@/components/layout/AppShell'))
const AuthGuard       = lazy(() => import('@/components/layout/AuthGuard'))
const Dashboard       = lazy(() => import('@/pages/Dashboard'))
const AddWebsite      = lazy(() => import('@/pages/onboarding/AddWebsite'))
const Progress        = lazy(() => import('@/pages/onboarding/Progress'))
const Overview        = lazy(() => import('@/pages/website/Overview'))
const ApprovalQueue   = lazy(() => import('@/pages/website/ApprovalQueue'))
const CalendarPage    = lazy(() => import('@/pages/website/CalendarPage'))
const BrandPage       = lazy(() => import('@/pages/website/BrandPage'))
const TemplatesPage   = lazy(() => import('@/pages/website/TemplatesPage'))
const SettingsPage    = lazy(() => import('@/pages/website/SettingsPage'))
const Billing         = lazy(() => import('@/pages/billing/Billing'))

function Fallback() {
  return <SkeletonPage variant="dashboard" />
}

/** Redirect logged-in users away from guest-only pages */
function GuestGuard() {
  const { user, isLoading } = useAuthStore()
  if (isLoading) return <SkeletonPage variant="form" rows={2} />
  return user ? <Navigate to="/dashboard" replace /> : <Outlet />
}

export const router = createBrowserRouter([
  { path: '/', element: <Landing /> },

  // Guest-only
  {
    element: <GuestGuard />,
    children: [
      { path: '/login',    element: <Login />    },
      { path: '/register', element: <Register /> },
    ],
  },

  // Auth-required app shell
  {
    element: (
      <Suspense fallback={<Fallback />}>
        <AuthGuard />
      </Suspense>
    ),
    // Catches auth/lazy-load failures before the shell mounts
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: (
          <Suspense fallback={<Fallback />}>
            <AppShell />
          </Suspense>
        ),
        // Catches any page-level render or chunk-load error inside the shell
        errorElement: <RouteErrorPage />,
        children: [
          // Dashboard (website list)
          {
            path: '/dashboard',
            element: (
              <Suspense fallback={<Fallback />}>
                <Dashboard />
              </Suspense>
            ),
          },

          // Onboarding
          {
            path: '/onboarding/add',
            element: (
              <Suspense fallback={<Fallback />}>
                <AddWebsite />
              </Suspense>
            ),
          },
          {
            path: '/onboarding/:websiteId/progress',
            element: (
              <Suspense fallback={<Fallback />}>
                <Progress />
              </Suspense>
            ),
          },

          // Per-website pages (websiteId param passed via :websiteId)
          {
            path: '/w/:websiteId/overview',
            element: (
              <Suspense fallback={<Fallback />}>
                <Overview />
              </Suspense>
            ),
          },
          {
            path: '/w/:websiteId/queue',
            element: (
              <Suspense fallback={<Fallback />}>
                <ApprovalQueue />
              </Suspense>
            ),
          },
          {
            path: '/w/:websiteId/calendar',
            element: (
              <Suspense fallback={<Fallback />}>
                <CalendarPage />
              </Suspense>
            ),
          },
          {
            path: '/w/:websiteId/brand',
            element: (
              <Suspense fallback={<Fallback />}>
                <BrandPage />
              </Suspense>
            ),
          },
          {
            path: '/w/:websiteId/templates',
            element: (
              <Suspense fallback={<Fallback />}>
                <TemplatesPage />
              </Suspense>
            ),
          },
          {
            path: '/w/:websiteId/settings',
            element: (
              <Suspense fallback={<Fallback />}>
                <SettingsPage />
              </Suspense>
            ),
          },

          // Billing
          {
            path: '/billing',
            element: (
              <Suspense fallback={<Fallback />}>
                <Billing />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
])
